// Pure helpers of the admin client (no DOM, no globals): request bodies from form fields,
// API error mapping, time and countdown text, reconnect backoff. Unit-tested in Node
// (site/test/unit/adminClient.test.ts); admin.ts wires them to the page.

/** How a form control's value is sent (data-type on the input). */
export type FieldType = 'string' | 'number' | 'int' | 'bool' | 'onoff' | 'nullable-int' | 'json';

export interface FieldEntry {
  /** Dotted path: "rateLimit.count", "zones.0.name", "availability.1". */
  readonly name: string;
  readonly type: FieldType;
  /** Raw value of the control ("" when empty). */
  readonly value: string;
  /** Checkbox state (type 'bool'). */
  readonly checked?: boolean;
  /** data-omit-empty: leave the field out when the value is "". */
  readonly omitEmpty?: boolean;
}

/** Converts one raw value; `undefined` means "leave the field out". */
export function coerce(e: FieldEntry): unknown {
  if (e.type === 'bool') return e.checked === true;
  // A checkbox standing for a Zone_State (light show steps).
  if (e.type === 'onoff') return e.checked === true ? 'ON' : 'OFF';
  const v = e.value.trim() === '' && e.type !== 'string' ? '' : e.value;
  if (v === '' && e.omitEmpty) return undefined;
  switch (e.type) {
    case 'number':
    case 'int': {
      if (v.trim() === '') return undefined;
      const n = Number(v);
      // An unparsable value is sent as text so the server names the field.
      return Number.isFinite(n) ? n : v;
    }
    case 'nullable-int': {
      if (v.trim() === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : v;
    }
    case 'json':
      try {
        return JSON.parse(v) as unknown;
      } catch {
        return v;
      }
    default:
      return e.value;
  }
}

/** Attributes of a light show step row that carry the step index (sequenceEditor.ts). */
export const STEP_ATTRS = ['name', 'id', 'for', 'aria-describedby', 'data-error-for'] as const;

/** Replaces the step index in a name / id ("steps.3.x" or "...-steps-3-x") with `index`. */
export function renumberStep(value: string, index: number): string {
  return value.replace(/(steps[.-])\d+(?=[.-])/g, '$1' + String(index));
}

/** Sets `value` at a dotted path; numeric segments create arrays. */
export function setPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let node: Record<string, unknown> | unknown[] = target;
  for (let i = 0; i < parts.length; i++) {
    const key = parts[i] as string;
    const last = i === parts.length - 1;
    const container = node as Record<string, unknown>;
    if (last) {
      container[key] = value;
      return;
    }
    const nextIsIndex = /^\d+$/.test(parts[i + 1] as string);
    let child = container[key];
    if (typeof child !== 'object' || child === null) {
      child = nextIsIndex ? [] : {};
      container[key] = child;
    }
    node = child as Record<string, unknown> | unknown[];
  }
}

/** JSON body for a form: every entry coerced and placed at its path. */
export function formBody(entries: readonly FieldEntry[]): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const e of entries) {
    if (e.name === '' || e.name === '_csrf') continue;
    const v = coerce(e);
    if (v !== undefined) setPath(body, e.name, v);
  }
  return body;
}

export interface ApiIssue {
  readonly field: string;
  readonly message: string;
}

/**
 * Candidate form field names for an API error field, most specific first:
 * "body.rateLimit.count" -> ["rateLimit.count", "rateLimit"]; "body" -> [].
 */
export function errorKeys(field: string): string[] {
  const path = field.replace(/^(body|params|querystring)\.?/, '');
  if (path === '') return [];
  const parts = path.split('.');
  const out: string[] = [];
  for (let i = parts.length; i > 0; i--) out.push(parts.slice(0, i).join('.'));
  return out;
}

const FIELD_MESSAGE = 'is not valid';

/** Field issues of an API error body (`errors`, or just the `fields` names). */
export function apiIssues(body: unknown): ApiIssue[] {
  if (typeof body !== 'object' || body === null) return [];
  const b = body as { errors?: unknown; fields?: unknown };
  if (Array.isArray(b.errors)) {
    return b.errors
      .filter(
        (e): e is { field: string; message?: unknown } =>
          typeof (e as { field?: unknown })?.field === 'string',
      )
      .map((e) => ({ field: e.field, message: typeof e.message === 'string' ? e.message : FIELD_MESSAGE }));
  }
  if (Array.isArray(b.fields)) {
    return b.fields
      .filter((f): f is string => typeof f === 'string')
      .map((f) => ({ field: f, message: FIELD_MESSAGE }));
  }
  return [];
}

const STATUS_TEXT: Readonly<Record<number, string>> = {
  401: 'Your session has ended. Please log in again.',
  403: 'The request was refused. Reload the page and try again.',
  404: 'That item no longer exists.',
  413: 'That is too large.',
  429: 'Too many requests. Please wait a moment.',
};

/** One-line message for a failed API call. */
export function apiMessage(status: number, body: unknown): string {
  const b = (typeof body === 'object' && body !== null ? body : {}) as {
    message?: unknown;
    retryAfterS?: unknown;
    error?: unknown;
  };
  let text: string;
  if (typeof b.message === 'string' && b.message !== '') text = b.message;
  else if (b.error === 'validation_failed') text = 'Please correct the highlighted fields.';
  else if (b.error === 'duplicate') text = 'That already exists.';
  else text = STATUS_TEXT[status] ?? 'Something went wrong (HTTP ' + String(status) + ').';
  if (typeof b.retryAfterS === 'number') text += ' Try again in ' + String(b.retryAfterS) + ' s.';
  return text;
}

// --- time ---------------------------------------------------------------------------

const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** "Thu, 24 Dec 2026, 21:05:07" in Site_Timezone, or `none` for null. */
export function formatInstant(t: number | null | undefined, none = 'never'): string {
  if (t === null || t === undefined) return none;
  return DATE_TIME.format(new Date(t));
}

function pad(n: number): string {
  return n < 10 ? '0' + String(n) : String(n);
}

/** "25:00:00" (hours may exceed 24), or "3 d 04:05:06" when `days` is set; never negative. */
export function formatDuration(ms: number, days = false): string {
  let s = Math.max(0, Math.floor(ms / 1000));
  let d = 0;
  if (days) {
    d = Math.floor(s / 86_400);
    s -= d * 86_400;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const hms = pad(h) + ':' + pad(m) + ':' + pad(sec);
  return d > 0 ? String(d) + ' d ' + hms : hms;
}

export interface CountdownInfo {
  readonly state: 'ONLINE' | 'OFFLINE';
  readonly label: string;
  readonly endsAt?: number;
  readonly startsAt?: number;
}

/** Countdown line from a countdown message; `now` is server time (clock offset applied). */
export function countdownText(c: CountdownInfo, now: number): string {
  const target = c.state === 'ONLINE' ? c.endsAt : c.startsAt;
  if (target === undefined) return c.state + ' – ' + c.label;
  const left = formatDuration(target - now, target - now >= 86_400_000);
  return c.state + ' – ' + c.label + ' (' + left + (c.state === 'ONLINE' ? ' left)' : ' to go)');
}

/** Reconnect delay before attempt n (0-based): 1 s doubling to 30 s (shared realtime client). */
export { backoffMs } from '../lib/realtime.js';

// --- health -------------------------------------------------------------------------

export interface HealthLike {
  readonly link: {
    readonly connected: boolean;
    readonly brokerConnected: boolean;
    readonly changedAt: number | null;
    readonly lastHeartbeatAt: number | null;
    readonly integrationVersion: string | null;
  };
  readonly zones: readonly {
    readonly n: number;
    readonly requested: string;
    readonly reported: string;
    readonly mismatch: boolean;
  }[];
  readonly pendingAll: unknown;
  readonly undelivered: boolean;
  readonly cameras: readonly {
    readonly n: number;
    readonly wanted: boolean;
    readonly status: string;
    readonly reason: string | null;
    readonly reportedAvailable: boolean | null;
    readonly lastSnapshotAt: number | null;
  }[];
  readonly notify: {
    readonly kind: string;
    readonly sending: boolean;
    readonly attempts: number;
    readonly lastSentAt: number | null;
    readonly lastError: string | null;
    readonly failed: boolean;
  } | null;
  /** Nightly backup status (Req 36.6); absent or null when not configured. */
  readonly backup?: {
    readonly state: string;
    readonly lastSuccessAt: number | null;
    readonly lastFailureAt: number | null;
    readonly lastFailureReason: string | null;
    readonly kept: number | null;
  } | null;
}

export function reportedText(r: string): string {
  return r === 'ON' || r === 'OFF' ? r : r === 'unavailable' ? 'unavailable' : 'unknown';
}

export function notifyText(n: HealthLike['notify']): string {
  if (n === null || n.kind === 'none') return 'No notification channel configured.';
  if (n.failed)
    return (
      'Notification failed after ' + String(n.attempts) + ' attempts (' + (n.lastError ?? 'error') + ').'
    );
  if (n.sending) return 'Sending a notification (attempt ' + String(Math.max(1, n.attempts)) + ')…';
  if (n.lastSentAt !== null) return 'Last notification sent ' + formatInstant(n.lastSentAt) + '.';
  return 'Channel ' + n.kind + ' ready; nothing sent yet.';
}

export function backupText(b: HealthLike['backup']): string {
  if (b === undefined || b === null) return 'Backup status is not configured.';
  const last = 'Last successful backup: ' + formatInstant(b.lastSuccessAt, 'none') + '.';
  if (b.state === 'unreadable') return 'The backup status file could not be read.';
  if (b.state === 'failed')
    return (
      'Last backup FAILED at ' +
      formatInstant(b.lastFailureAt) +
      (b.lastFailureReason ? ' (' + b.lastFailureReason + ')' : '') +
      '. ' +
      last
    );
  if (b.state === 'ok') return last + (b.kept !== null ? ' ' + String(b.kept) + ' backups kept.' : '');
  return 'No backups recorded yet.';
}

/**
 * Text for every live element of the health view and the dashboard strip, keyed like
 * the `data-h` attributes the server renders (http/adminViews.ts uses the same keys).
 */
export function healthTexts(h: HealthLike): Record<string, string> {
  const out: Record<string, string> = {
    'link.status': h.link.connected ? 'connected' : 'disconnected',
    'link.broker': h.link.brokerConnected ? 'connected' : 'disconnected',
    'link.heartbeat': formatInstant(h.link.lastHeartbeatAt, 'none received since the site started'),
    'link.changed': formatInstant(h.link.changedAt, 'not since the site started'),
    'link.version': h.link.integrationVersion ?? 'unknown',
    undelivered: h.undelivered
      ? 'The last all-on / all-off has not been delivered to Home Assistant yet.'
      : '',
    mismatched: zonesText(h.zones.filter((z) => z.mismatch).map((z) => z.n)),
    notify: notifyText(h.notify),
    backup: backupText(h.backup),
  };
  for (const z of h.zones) {
    const k = 'zone.' + String(z.n) + '.';
    out[k + 'requested'] = z.requested;
    out[k + 'reported'] = reportedText(z.reported);
    out[k + 'mismatch'] = z.mismatch ? 'mismatch' : 'ok';
  }
  for (const c of h.cameras) {
    const k = 'camera.' + String(c.n) + '.';
    out[k + 'status'] = c.status === 'LIVE' ? 'LIVE' : 'OFFLINE' + (c.reason ? ' (' + c.reason + ')' : '');
    out[k + 'wanted'] = c.wanted ? 'yes' : 'no';
    out[k + 'available'] =
      c.reportedAvailable === null ? 'no report' : c.reportedAvailable ? 'available' : 'unavailable';
    out[k + 'snapshot'] = formatInstant(c.lastSnapshotAt, 'none received');
  }
  return out;
}

export function zonesText(ns: readonly number[]): string {
  return ns.length === 0 ? 'none' : 'zones ' + ns.join(', ');
}
